import type { OpCo, Tracker } from "@infinitaslearning/module-data-analyticstracker";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import type { Config } from "@/config";
import { config } from "@/config";
import { ANALYTICS_PLATFORM, ANALYTICS_PLATFORM_VARIANT } from "@/lib/constants";
import { useAuth } from "@/providers/authProvider";

type TrackerEnvironment = "local" | "development" | "acceptance" | "production";

const environmentMapping: Record<Config["ilEnv"], TrackerEnvironment> = {
  local: "local",
  development: "development",
  staging: "acceptance",
  production: "production",
};

const AnalyticsContext = createContext<Tracker | null>(null);

interface AnalyticsProviderProps {
  children: ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const [tracker, setTracker] = useState<Tracker | null>(null);
  const { user } = useAuth();

  const trackerEnvironment = environmentMapping[config.ilEnv] ?? "local";
  const userSub = user?.sub;
  // Cast to OpCo — tenantSlug is forwarded as-is; if the value doesn't match
  // a known OpCo slug the tracker will accept it as a custom string.
  const userOpco = user?.tenantSlug as OpCo | undefined;

  useEffect(() => {
    // Skip tracker creation in local development — matches the Clarity pattern
    // and avoids COEP/CORS issues against the analytics endpoint.
    if (typeof window === "undefined" || config.ilEnv === "local") {
      return;
    }

    import("@infinitaslearning/module-data-analyticstracker")
      .then(({ createTracker }) => {
        const newTracker = createTracker({
          platform: ANALYTICS_PLATFORM,
          environment: trackerEnvironment,
          platformVariant: ANALYTICS_PLATFORM_VARIANT,
          ...(userOpco && { opCo: userOpco }),
        });

        if (userSub) {
          newTracker.identify({ id: userSub });
        }

        setTracker(newTracker);
      })
      .catch((error) => {
        console.error("Failed to load analytics tracker:", error);
      });
  }, [userSub, userOpco, trackerEnvironment]);

  return <AnalyticsContext.Provider value={tracker}>{children}</AnalyticsContext.Provider>;
}

export function useAnalyticsTracker(): Tracker | null {
  return useContext(AnalyticsContext);
}
